import {useParams} from "react-router";
import { useDispatch, useSelector } from "react-redux";
import React, { useContext, useEffect, useRef, useState } from "react";
import {
	getCategoriesStatus,
	getItemCategories,
	getProductById,
	putProductGroup,
	selectGetCategoriesById,
	selectGetProductsById,
} from "../../redux/ProductsSlice";
import Grid from "@mui/material/Grid2";
import { Link } from "react-router-dom";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import NotFound from "../../Components/Error/NotFound";
import GeneralError from "../../Components/Error/GeneralError";
import ProductDetailsView from "../../Components/Products/ProductDetailsView";
import SaveIcon from "@mui/icons-material/Save";
import {
	alphaNumericValidator,
	checkAllValid,
} from "../../Helpers/ValidationHelpers";
import { putProductItem } from "../../redux/ProductsSlice";
import ValidatedTextField from "../../Components/Inputs/ValidatedTextField";
import TrimObjectStrings from "../../Helpers/TrimObjectStrings";
import { IsOnboardedContext } from "../../Contexts/IsOnboardedContext";
import ProductGroupCategoriesView from "../../Components/Categories/ProductGroupCategoriesView";
import ProductParameterDataGrid from "../../Components/Parameters/ProductParameterDataGrid";
import EditIcon from "@mui/icons-material/Edit";
import equal from "fast-deep-equal";
import Breadcrumb from "../../Components/Navigation/Breadcrumb";
import LoadingOverlay from "../../Components/Loading/LoadingOverlay";
import Box from "@mui/material/Box";
import {useSnackbar} from "notistack";
import RenderGoBackButton from "../../Helpers/RenderGoBackButton";

const ProductDetails = () => {
	const { id } = useParams();
	const context = useContext(IsOnboardedContext);
	const productFromRedux = useSelector(selectGetProductsById)[id];
	const [isLoading, setIsLoading] = useState(true);

	const [product, setProduct] = useState(productFromRedux);
	const [fieldRows, setFieldRows] = useState(productFromRedux?.fields ?? []);
	const [edit, setEdit] = useState(false);
	const [activeForm, setActiveForm] = useState(null);
	const [initialProduct, setInitialProduct] = useState();
	const [isSubmitting, setIsSubmitting] = useState(false);
	
	const { enqueueSnackbar } = useSnackbar();
	const [productError, setProductError] = useState(null);
	const formValid = useRef({});

	const dispatch = useDispatch();

	const categories = useSelector(selectGetCategoriesById);
	const categoriesStatus = useSelector(getCategoriesStatus);
	const [pathMap, setPathMap] = useState({});

	useEffect(() => {
		if (categoriesStatus === "idle") {
			dispatch(getItemCategories());
		}
	}, [dispatch, categoriesStatus]);

	useEffect(() => {
		const fetchProduct = async () => {
			try {
				const response = await dispatch(getProductById(id)).unwrap();
				setProduct(response);
				setFieldRows(response.fields);
				setIsLoading(false);
				document.title = `${response.name} - CPC`;
			} catch (error) {
				setProductError(error);
				setIsLoading(false);
				document.title = "CPC";
			}
		};

		document.title = "Loading...";
		if (productFromRedux) {
			setPathMap({ [id]: productFromRedux.name });
			setIsLoading(false);
			document.title = `${productFromRedux.name} - CPC`;
			return;
		}

		setIsLoading(true);
		fetchProduct();
	}, [dispatch, productFromRedux, id]);
	
	useEffect(() => {
		setProduct(productFromRedux);
		setFieldRows(productFromRedux?.fields);
	}, [productFromRedux])

	const handleEdit = () => {
		setEdit(true);
		setInitialProduct({
			...product,
		});

		if (product.productType === "Item") {
			setActiveForm({
				...product,
			});

			formValid.current = {
				name: true,
				description: true,
				businessCode: true,
				fields: product.fields.reduce((acc, field) => {
					acc[field.id ?? field.tempId] = {};
					for (const prop in field) {
						if (field.hasOwnProperty(prop)) {
							if (prop === "enumOptions") {
								acc[field.id ?? field.tempId ?? field.name][prop] = field[
									prop
								]?.reduce((dropdownAcc, dropdownValue) => {
									dropdownAcc[dropdownValue.id] = true;
									return dropdownAcc;
								}, {});
							} else {
								acc[field.id ?? field.tempId][prop] = true;
							}
						}
					}
					return acc;
				}, {}),
			};
		} else if (product.productType === "Group") {
			setActiveForm({
				...product,
			});

			formValid.current = {
				name: true,
				description: true,
				businessCode: true,
			};
		}
	};

	useEffect(() => {
		if (activeForm) {
			if (product.productType === "Item") {
				setActiveForm({
					...activeForm,
					fields: fieldRows,
				});
			}
		}
	}, [fieldRows]);

	const handleCancelEdit = () => {
		setEdit(false);
		setFieldRows(initialProduct.fields);
		setActiveForm(null);
	};

	function handleSave() {
		if (equal(initialProduct, activeForm)) {
			setIsSubmitting(false);
			setEdit(false);
			setInitialProduct(activeForm);
			return;
		}
		if (checkAllValid(formValid.current)) {
			setIsSubmitting(true);
			if (product.productType === "Item") {
				dispatch(
					putProductItem(
						TrimObjectStrings({
							id: id,
							payload: {
								...activeForm,
								fields: fieldRows,
								updatedBy: context.userName,
							},
						})
					)
				)
					.unwrap()
					.then((response) => {
						setProduct({
							...response,
						});
						setFieldRows(
							response.fields.map((field) => {
								return {
									...field,
									editable: false,
								};
							})
						);
						setIsSubmitting(false);
						setEdit(false);
						setInitialProduct(response);
						enqueueSnackbar("Product successfully updated!", {
							variant: "success",
							autoHideDuration: 3000,
						});
					})
					.catch((error) => {
						console.error("Failed to update product", error);
						setIsSubmitting(false);
						const errorMessage = error.status === 409
							? `Failed to update product. ${error.detail}.`
							: "Failed to update product. Please try again."
						enqueueSnackbar(errorMessage, {
							variant: "error",
							autoHideDuration: 3000,
						});
					});
			} else if (product.productType === "Group") {
				const cleanedAllowedCategories = activeForm.allowedCategories.map(
					({ newCategory, ...rest }) => rest
				);
				const payloadToSend = {
					...activeForm,
					allowedCategories: cleanedAllowedCategories,
					updatedBy: context.userName,
				};

				dispatch(
					putProductGroup(
						TrimObjectStrings({
							id: id,
							payload: payloadToSend,
						})
					)
				)
					.unwrap()
					.then((response) => {
						setProduct(response);
						setIsSubmitting(false);
						setEdit(false);
						setInitialProduct(response);
						enqueueSnackbar("Product successfully updated!", {
							variant: "success",
							autoHideDuration: 3000,
						});
					})
					.catch((error) => {
						console.error("Failed to update product", error);
						setIsSubmitting(false);
						const errorMessage = error.status === 409
							? `Failed to update product. ${error.detail}.`
							: "Failed to update product. Please try again."
						enqueueSnackbar(errorMessage, {
							variant: "error",
							autoHideDuration: 3000,
						});
					});
			}
		}
	}

	if (product && !productError) {
		return (
			<>
				{(isSubmitting || isLoading) && <LoadingOverlay />}
				<Breadcrumb pathMap={pathMap} />
				<Grid
					container
					spacing={3}
				>
					{!edit ? (
						<Grid size={{ xs: 12, sm: 6, md: 7 }}>
							<Typography variant='h2'>{product.name}</Typography>
						</Grid>
					) : (
						<Grid size={{ xs: 12, sm: 8 }}>
							<ValidatedTextField
								id={"name"}
								label={"Name"}
								validator={alphaNumericValidator}
								onChange={(isValid) => (formValid.current.name = isValid)}
								setActiveForm={setActiveForm}
								isSubmitting={isSubmitting}
								initialValue={product.name}
								fieldPath={["name"]}
								infoAdornment={null}
								removeHelperTextPadding={true}
							/>
						</Grid>
					)}
					{!edit ? (
						<>
							<Grid
								size={{ xs: 12, sm: 6, md: 5 }}
								sx={{
									display: "flex",
									alignItems: "center",
									justifyContent: { xs: "left", sm: "right" },
								}}
							>
								<Button
									variant='contained'
									onClick={handleEdit}
									startIcon={<EditIcon />}
									className='edit-button'
									sx={{
										mr: 2,
									}}
								>
									Edit
								</Button>
								<Link to='/projects/new'>
									<Button
										variant='contained'
										color='primary'
									>
										Create New Project
									</Button>
								</Link>
							</Grid>
						</>
					) : (
						<Grid
							size={{ xs: 12, sm: 4 }}
							sx={{
								display: "flex",
								alignItems: "center",
								justifyContent: { xs: "left", sm: "right" },
							}}
						>
						</Grid>
					)}
					<Grid size={{ xs: 12 }}>
						<ProductDetailsView
							product={product}
							edit={edit}
							setActiveForm={setActiveForm}
							formValid={formValid}
							isSubmitting={isSubmitting}
						/>
					</Grid>
					<Grid size={{ xs: 12 }}>
						{product.productType === "Item" && (
							<>
								<Typography
									variant='h4'
									sx={{ my: 1 }}
								>
									Parameters
								</Typography>
								<ProductParameterDataGrid
									rows={fieldRows}
									setRows={setFieldRows}
									isEditMode={edit}
									isProductDetailsPage
								/>
								{edit && (
									<Box sx={{ my: 2 }}>
										<Button
											variant='contained'
											className='save-button'
											onClick={handleSave}
											disabled={!checkAllValid(formValid.current)}
											startIcon={<SaveIcon />}
											sx={{
												mr: 2,
											}}
										>
											Save
										</Button>
										{RenderGoBackButton(equal(initialProduct, activeForm), handleCancelEdit)}
									</Box>
								)}
							</>
						)}
						{product.productType === "Group" && (
							<>
								<ProductGroupCategoriesView
									allCategories={categories}
									product={product}
									edit={edit}
									setActiveForm={setActiveForm}
									activeForm={activeForm}
									isSubmitting={isSubmitting}
								/>
								{edit && (
									<Box sx={{ my: 3 }}>
										<Button
											variant='contained'
											className='save-button'
											onClick={handleSave}
											disabled={
												!checkAllValid(formValid.current) || isSubmitting
											}
											startIcon={<SaveIcon />}
											sx={{
												mr: 2,
											}}
										>
											Save
										</Button>
										{RenderGoBackButton(equal(initialProduct, activeForm), handleCancelEdit)}
									</Box>
								)}
							</>
						)}
					</Grid>
				</Grid>
			</>
		);
	} else if (productError !== null) {
		if (productError.status === 404) {
			return (
				<div>
					<NotFound />
				</div>
			);
		} else {
			return (
				<div>
					<GeneralError />
				</div>
			);
		}
	} else {
		return <LoadingOverlay />;
	}
};

export default ProductDetails;
